import Decimal from "decimal.js";
import { Loader2 } from "lucide-react";
import type { CurrencyCode } from "@forgepoint/shared";
import { formatCurrency, formatUnitPrice } from "@/lib/utils";

/** One quantity column's per-unit value, or null when it is unavailable. */
export interface PriceRowCell {
  quantity: string;
  /** Per-unit value as a decimal string, or null to render as a dash. */
  value: string | null;
}

interface PriceRowProps {
  label: string;
  cells: PriceRowCell[];
  isLoading: boolean;
  /**
   * Visual treatment: "muted" for indented sub-steps (pricing lines), "total"
   * for the emphasized final price row, "default" otherwise.
   */
  variant?: "default" | "muted" | "total";
  /**
   * When true, renders each value as a signed increment (a leading "+" on
   * non-negative values), so waterfall steps read as additions.
   */
  signed?: boolean;
  /** Formats the cells in this currency and appends its code. Omit for the base currency. */
  currency?: CurrencyCode;
}

const LABEL_BASE =
  "py-2 px-4 text-sm sticky left-0 z-10 border-r border-border";
const CELL_BASE =
  "py-2 px-4 text-right text-sm border-l border-border tabular-nums";

/**
 * A single row of the unit-price waterfall: a sticky label plus one two-line
 * cell per quantity (per-unit value with an extended total beneath it). Used
 * for the cost, margin, pricing-line, and total-price rows so they share
 * identical layout, loading, and empty handling.
 */
export function PriceRow({
  label,
  cells,
  isLoading,
  variant = "default",
  signed = false,
  currency,
}: PriceRowProps) {
  const isTotal = variant === "total";
  const isMuted = variant === "muted";
  const bg = isTotal ? "bg-accent" : "bg-card";
  const bottomBorder = isTotal ? "" : " border-b border-b-border";
  const mutedText = isMuted ? " text-muted-foreground" : "";

  return (
    <tr className={isTotal ? "bg-accent font-semibold" : undefined}>
      <td className={`${LABEL_BASE} ${bg}${bottomBorder}${mutedText}`}>
        {label}
      </td>
      {cells.map(({ quantity, value }) => (
        <td
          key={quantity}
          className={`${CELL_BASE}${bottomBorder}${mutedText}`}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin inline" />
          ) : value === null ? (
            "—"
          ) : (
            <PriceCellValue
              value={value}
              quantity={quantity}
              signed={signed}
              extendedFontNormal={isTotal}
              currency={currency}
            />
          )}
        </td>
      ))}
    </tr>
  );
}

interface PriceCellValueProps {
  value: string;
  quantity: string;
  signed: boolean;
  extendedFontNormal: boolean;
  currency: CurrencyCode | undefined;
}

function PriceCellValue({
  value,
  quantity,
  signed,
  extendedFontNormal,
  currency,
}: PriceCellValueProps) {
  const unit = new Decimal(value);
  const extended = unit.times(quantity);
  const unitText = withSign(formatUnitPrice(value, currency), unit, signed);
  const extendedText = withSign(
    formatCurrency(extended.toString(), currency),
    extended,
    signed,
  );
  const extendedClass = `text-[10px] text-muted-foreground${
    extendedFontNormal ? " font-normal" : ""
  }`;

  return (
    <>
      <div>{withCode(unitText, currency)}/ea</div>
      <div className={extendedClass}>{withCode(extendedText, currency)}</div>
    </>
  );
}

/** Prefixes a "+" on non-negative values when signed; negatives keep their "-". */
function withSign(formatted: string, raw: Decimal, signed: boolean): string {
  return signed && raw.greaterThanOrEqualTo(0) ? `+${formatted}` : formatted;
}

/** Appends the ISO code when the row renders a non-base currency. */
function withCode(
  formatted: string,
  currency: CurrencyCode | undefined,
): string {
  return currency ? `${formatted} ${currency}` : formatted;
}
